import style from "./style.scss";
import { h } from "preact";
import { useState, useEffect } from "preact/hooks";
import { selectActiveCreative, PromoPayload } from "../../data/ads/catalog";
import { getBrowserCountry } from "../../utils/getBrowserCountry";

const affiliateAdsEnabled = process.env.AFFILIATE_ads_enabled === "true";

export interface AffiliateAdProps {
  brand: string;
  size?: string;
  dismissible?: boolean;
}

export function AffiliateAd({
  brand,
  size = "leaderboard",
  dismissible = true,
}: AffiliateAdProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  // Static Pre-render Default (US)
  const [promo, setPromo] = useState<PromoPayload | null>(() => {
    if (!affiliateAdsEnabled) return null;
    return selectActiveCreative(brand, size, "US");
  });

  useEffect(() => {
    if (!affiliateAdsEnabled) return;

    // Client-side selection (runs for ALL countries including US)
    const userCountry = getBrowserCountry();
    const active = selectActiveCreative(brand, size, userCountry);

    // Only update state if the candidate changed to avoid unnecessary re-renders
    setPromo((prev) => {
      if (!prev && !active) return null;
      if (prev && active && prev.id === active.id) return prev;
      return active;
    });
  }, [brand, size]);

  // // GA4 view_promotion event on mount or creative selection update
  // useEffect(() => {
  //   if (promo && typeof gtag !== "undefined") {
  //     gtag("event", "view_promotion", {
  //       network: promo.network,
  //       brand: brand,
  //       advertiser_id: promo.advertiserId,
  //       promotion_id: promo.id,
  //       promotion_region: promo.region,
  //       promotion_tag_id: promo.tagId,
  //       promotion_type: size,
  //       promotion_size: `${promo.width}x${promo.height}`,
  //       target_url: promo.targetUrl,
  //       destination_url: promo.clickUrl,
  //       transport: "beacon",
  //     });
  //   }
  // }, [promo?.id]);

  if (!affiliateAdsEnabled || !promo || isDismissed) {
    return null;
  }

  const handleClick = () => {
    if (typeof gtag !== "undefined") {
      gtag("event", "select_promotion", {
        network: promo.network,
        brand: brand,
        advertiser_id: promo.advertiserId,
        promotion_id: promo.id,
        promotion_region: promo.region,
        promotion_tag_id: promo.tagId,
        promotion_type: size,
        promotion_size: `${promo.width}x${promo.height}`,
        target_url: promo.targetUrl,
        destination_url: promo.clickUrl,
        transport: "beacon",
      });
    }
  };

  const handleDismiss = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDismissed(true);

    if (typeof gtag !== "undefined") {
      gtag("event", "dismiss_promotion", {
        network: promo.network,
        brand: brand,
        advertiser_id: promo.advertiserId,
        promotion_id: promo.id,
        promotion_region: promo.region,
        promotion_tag_id: promo.tagId,
        promotion_type: size,
        promotion_size: `${promo.width}x${promo.height}`,
        target_url: promo.targetUrl,
        destination_url: promo.clickUrl,
      });
    }
  };

  return (
    <div class={style.adWrapper}>
      <span class={style.adLabel}>Affiliate Offer</span>
      <div class={style.adContainer}>
        {dismissible && (
          <button
            type="button"
            class={style.closeButton}
            onClick={handleDismiss}
            aria-label="Hide offer"
          >
            <svg
              viewBox="0 0 24 24"
              width="12"
              height="12"
              stroke="currentColor"
              stroke-width="3"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
        <a
          href={promo.clickUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          data-network={promo.network}
          data-region={promo.region}
          onClick={handleClick}
          class={style.adLink}
        >
          <img
            src={promo.imageUrl}
            referrerpolicy="no-referrer"
            width={promo.width}
            height={promo.height}
            alt={`${brand} Special Offer`}
            loading="lazy"
            class={style.adImage}
          />
        </a>
      </div>
    </div>
  );
}

export default AffiliateAd;
